import { useState } from "react";
import type { Member } from "@/services/types";
import { cn } from "@/utils/cn";
import { AccountTab } from "./AccountTab";
import { ActivityTab } from "./ActivityTab";

interface PassbookAndLedgerTabProps {
  member: Member;
  onUpdateMember: (member: Member) => void;
  isViewer?: boolean;
  defaultSubView?: "wallet" | "chit_ledger";
}

export function PassbookAndLedgerTab({
  member,
  onUpdateMember,
  isViewer,
  defaultSubView = "wallet",
}: PassbookAndLedgerTabProps) {
  const [subView, setSubView] = useState<"wallet" | "chit_ledger">(defaultSubView);

  const walletBalance = member.accountBalance ?? 0;

  return (
    <div className="space-y-4">
      {/* Sub-view Switcher Bar */}
      <div className="no-print flex flex-col gap-3 rounded-xl border border-background-200 bg-background-50 p-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex rounded-lg bg-background-200/90 p-1">
          <button
            type="button"
            role="tab"
            aria-selected={subView === "wallet"}
            onClick={() => setSubView("wallet")}
            className={cn(
              "flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition-all focus:outline-none",
              subView === "wallet"
                ? "bg-white text-foreground-950 shadow-sm"
                : "text-foreground-600 hover:text-foreground-900"
            )}
          >
            <i className="ri-wallet-3-line text-emerald-600" />
            <span>Advance Wallet Passbook</span>
            {walletBalance !== 0 ? (
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                  walletBalance < 0 ? "bg-danger-100 text-danger-800" : "bg-emerald-100 text-emerald-800"
                )}
              >
                ₹{walletBalance.toLocaleString("en-IN")}
              </span>
            ) : null}
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={subView === "chit_ledger"}
            onClick={() => setSubView("chit_ledger")}
            className={cn(
              "flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition-all focus:outline-none",
              subView === "chit_ledger"
                ? "bg-white text-foreground-950 shadow-sm"
                : "text-foreground-600 hover:text-foreground-900"
            )}
          >
            <i className="ri-file-list-3-line text-primary-600" />
            <span>Chit Fund Ledger</span>
          </button>
        </div>

        <div className="px-2 text-xs text-foreground-500">
          {subView === "wallet"
            ? "Prepaid advance deposits, cashouts, installment deductions & receipts"
            : "Group installment demands, payments collected, dividend credits & late fees"}
        </div>
      </div>

      {/* Render Active View */}
      {subView === "wallet" ? (
        <AccountTab member={member} onUpdateMember={onUpdateMember} isViewer={isViewer} />
      ) : (
        <ActivityTab member={member} />
      )}
    </div>
  );
}
